import api from '../lib/api';

const BASE = '/api/v1/admin/exports';

/** What a download comes back as: the file, the name the server gave it, and how many rows it holds. */
export interface ExportFile {
  blob: Blob;
  filename: string;
  rows?: number;
}

/** A failed export. `message` is safe to show; `status` lets callers treat "nothing to export" (404) differently from a real failure. */
export class ExportError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'ExportError';
  }
}

/** Filters of the Analytics screen. Empty values are dropped before sending. */
export interface AnalyticsExportFilters {
  from?: string;
  to?: string;
  userId?: string;
  projectId?: string;
}

/** Filters of the All Entries screen. Empty values are dropped before sending. */
export interface EntryExportFilters {
  id?: string;
  from?: string;
  to?: string;
  userId?: string;
  projectId?: string;
  status?: string;
}

/** The Customer Timesheet takes the same filters; it is always approved entries only. */
export interface TimesheetExportFilters extends Omit<EntryExportFilters, 'status'> {
  clientName?: string;
}

export interface UserExportFilters {
  name?: string;
  jobTitleId?: string;
  includeDeleted?: boolean;
  sortBy?: 'name' | 'manager' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export interface ProjectExportFilters {
  name?: string;
  sortBy?: 'name' | 'client' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export interface DangerZoneExportFilters {
  from?: string;
  to?: string;
  userId?: string;
  projectId?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The filename in a Content-Disposition header: the exact UTF-8 name if present, else the quoted one, else `fallback`. */
export function filenameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      // malformed escape sequence: fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  return plain?.[1].trim() || fallback;
}

function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** Turns whatever axios threw into an ExportError, decoding the JSON error the server sent as a blob. */
async function toExportError(error: unknown): Promise<ExportError> {
  const res = (error as { response?: { status?: number; data?: unknown } } | undefined)?.response;
  if (!res) return new ExportError('Could not reach the server. Please try again.');

  let message = '';
  if (res.data instanceof Blob) {
    try {
      message = JSON.parse(await readBlobText(res.data)).error ?? '';
    } catch {
      // body wasn't JSON
    }
  }
  return new ExportError(message || 'Export failed. Please try again.', res.status);
}

function clean(params: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '') as [string, string][],
  );
}

async function download(path: string, params: object, fallbackName: string): Promise<ExportFile> {
  try {
    const res = await api.get<Blob>(`${BASE}${path}`, { params: clean(params), responseType: 'blob' });
    const rows = Number(res.headers?.['x-row-count']);
    return {
      blob: res.data,
      filename: filenameFromDisposition(res.headers?.['content-disposition'], fallbackName),
      rows: Number.isFinite(rows) ? rows : undefined,
    };
  } catch (error) {
    throw await toExportError(error);
  }
}

/** Hands a downloaded file to the browser's save flow. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const exportsApi = {
  /** The Entry List for the given filters, as an xlsx workbook or a plain CSV. */
  entries: (filters: EntryExportFilters, format: 'xlsx' | 'csv' = 'xlsx') =>
    download('/entries', { ...filters, format }, `entries.${format}`),

  /** The formatted customer timesheet, one sheet per employee (xlsx, approved entries only). */
  customerTimesheet: (filters: TimesheetExportFilters) =>
    download('/customer-timesheet', filters, 'timesheet.xlsx'),

  /** Everyone the Users screen filters to (xlsx). */
  users: (filters: UserExportFilters) => download('/users', filters, 'users.xlsx'),

  /** Weeks whose logged hours differ from the weekly target, for the Danger Zone screen's filters (xlsx). */
  dangerZone: (filters: DangerZoneExportFilters) => download('/danger-zone', filters, 'danger-zone.xlsx'),

  /** Every project the Projects screen filters to, with its members (xlsx). */
  projects: (filters: ProjectExportFilters) => download('/projects', filters, 'projects.xlsx'),

  /** The Analytics page as a workbook: KPI figures, the four charts and the data behind them (xlsx). */
  analytics: (filters: AnalyticsExportFilters) => download('/analytics', filters, 'analytics.xlsx'),
};
